import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { FaBars, FaTimes, FaMobileAlt, FaTv } from "react-icons/fa";
import { Link } from "react-router-dom";

/* ---------------- NAVBAR ---------------- */

const TopNav = styled.div`
  position: sticky;
  top: 0;
  z-index: 100;
  background: rgba(8, 10, 16, 0.92);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
`;

const NavInner = styled.div`
  max-width: 1100px;
  margin: auto;
  padding: 14px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Brand = styled(Link)`
  font-size: 18px;
  font-weight: 700;
  color: #ffb97b;
  text-decoration: none;
`;

const NavLinks = styled.div`
  display: flex;
  gap: 22px;

  a {
    color: #ddd;
    text-decoration: none;
    font-weight: 500;

    &:hover {
      color: #ffb97b;
    }
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const Burger = styled.div`
  display: none;
  font-size: 22px;
  color: #fff;
  cursor: pointer;

  @media (max-width: 768px) {
    display: block;
  }
`;

const MobileMenu = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: #080a10;
  z-index: 200;
  padding: 90px 30px;
`;

const CloseBtn = styled.div`
  position: absolute;
  top: 22px;
  right: 22px;
  font-size: 24px;
  color: #fff;
  cursor: pointer;
`;

const MobileLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 26px;

  a {
    font-size: 20px;
    font-weight: 600;
    color: #fff;
    text-decoration: none;

    &:hover {
      color: #ffb97b;
    }
  }
`;

/* ---------------- PAGE ---------------- */

const Page = styled.div`
  min-height: 100vh;
  background: radial-gradient(circle at top, #141823, #080a10);
  color: #fff;
  padding: 140px 20px 90px;
`;

const Container = styled.div`
  max-width: 1000px;
  margin: auto;
`;

const Title = styled.h1`
  font-size: 42px;
  text-align: center;
  margin-bottom: 12px;

  span {
    color: #ffb97b;
  }
`;

const Subtitle = styled.p`
  text-align: center;
  opacity: 0.85;
  margin-bottom: 60px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 24px;
`;

const Card = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  padding: 28px;
  text-align: center;
  backdrop-filter: blur(12px);
  transition: 0.3s;

  &:hover {
    transform: translateY(-6px);
    border-color: #ffb97b;
  }

  .icon {
    font-size: 40px;
    margin-bottom: 12px;
    color: #ffb97b;
  }

  h3 {
    margin-bottom: 10px;
  }

  p {
    opacity: 0.8;
    font-size: 14px;
    line-height: 1.5;
  }
`;

const CTA = styled.section`
  margin-top: 80px;
  text-align: center;
  padding: 60px 20px;
  background: linear-gradient(135deg, #ce8b54, #1f1f1f);
  border-radius: 20px;

  h2 {
    margin-bottom: 10px;
  }

  p {
    opacity: 0.9;
    margin-bottom: 20px;
  }

  a {
    padding: 14px 30px;
    background: #fff;
    color: #000;
    border-radius: 30px;
    text-decoration: none;
    font-weight: 700;
  }
`;

/* ---------------- COMPONENT ---------------- */

const Services = () => {
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const services = [
    {
      icon: <FaMobileAlt />,
      title: "Mobile Recharge ",
      desc: "Instant mobile recharge API with high success rate and real-time tracking.",
    },
    {
      icon: <FaTv />,
      title: "DTH Recharge",
      desc: "Fast and reliable DTH recharge API for all major operators.",
    },
  ];

  return (
    <>
      {/* NAV */}
      <TopNav>
        <NavInner>
          <Brand to="/">API EXCHANGE</Brand>

          <NavLinks>
            <Link to="/">Home</Link>
            <Link to="/login">Login</Link>
            <Link to="/register">Register</Link>
            <Link to="/contact">Contact</Link>
          </NavLinks>

          <Burger onClick={() => setMenuOpen(true)}>
            <FaBars />
          </Burger>
        </NavInner>
      </TopNav>

      {/* MOBILE MENU */}
      <AnimatePresence>
        {menuOpen && (
          <MobileMenu
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.3 }}
          >
            <CloseBtn onClick={() => setMenuOpen(false)}>
              <FaTimes />
            </CloseBtn>

            <MobileLinks>
              <Link to="/" onClick={() => setMenuOpen(false)}>Home</Link>
              <Link to="/login" onClick={() => setMenuOpen(false)}>Login</Link>
              <Link to="/register" onClick={() => setMenuOpen(false)}>Register</Link>
              <Link to="/contact" onClick={() => setMenuOpen(false)}>Contact</Link>
            </MobileLinks>
          </MobileMenu>
        )}
      </AnimatePresence>

      {/* PAGE */}
      <Page>
        <Container>
          <Title>
            Our <span>Services</span>
          </Title>

          <Subtitle>
            Powerful recharge & API solutions for modern businesses
          </Subtitle>

          <Grid>
            {services.map((s, i) => (
              <Card key={i}>
                <div className="icon">{s.icon}</div>
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
              </Card>
            ))}
          </Grid>

          <CTA>
            <h2>Start Using Our APIs Today</h2>
            <p>Fast integration • Secure • Scalable infrastructure</p>
            <Link to="/register">Create Account</Link>
          </CTA>
        </Container>
      </Page>
    </>
  );
};

export default Services;